import React from "react";
import { Section } from "@/types/models/page";
import { generateImageSrc } from "@/utils/helper";

interface SectionCardProps {
  section: Section;
  onClick: () => void;
  onDragStart?: (e: React.DragEvent) => void;
}

export function SectionCard({
  section,
  onClick,
  onDragStart,
}: SectionCardProps) {
  const previewImageUrl = section.preview_image_asset
    ? generateImageSrc(section.preview_image_asset.path)
    : null;

  return (
    <div
      className="bg-card dark:bg-auth-card rounded-lg border border-border dark:border-auth-background hover:border-primary dark:hover:border-primary hover:shadow-md transition-all duration-200 cursor-pointer group overflow-hidden"
      onClick={onClick}
      draggable
      onDragStart={onDragStart}
    >
      {/* Preview Image */}
      <div className="relative w-full h-32 bg-muted dark:bg-auth-background overflow-hidden">
        {previewImageUrl ? (
          <>
            <img
              src={previewImageUrl}
              alt={section.name}
              width={200}
              height={128}
              className="w-full h-full object-contain bg-muted/40 p-2 transition-transform duration-200 group-hover:scale-[1.02]"
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                e.currentTarget.style.display = "none";
                e.currentTarget.parentElement?.classList.add(
                  "flex",
                  "items-center",
                  "justify-center",
                );
                const placeholder = document.createElement("div");
                placeholder.className =
                  "flex flex-col items-center justify-center text-muted-foreground";
                placeholder.innerHTML = `
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
                  </svg>
                  <span class="text-xs mt-1">No Preview</span>
                `;
                e.currentTarget.parentElement?.appendChild(placeholder);
              }}
            />

            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-background/90 opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100 dark:bg-auth-card/95">
              <div className="h-full w-full p-3">
                <img
                  src={previewImageUrl}
                  alt={`${section.name} full preview`}
                  className="h-full w-full object-contain drop-shadow-lg"
                />
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
            </svg>
            <span className="text-xs mt-1">No Preview</span>
          </div>
        )}

        {/* Overlay indicators */}
        <div className="absolute top-2 right-2 flex items-center space-x-1">
          {section.global && (
            <span className="text-xs bg-primary/80 text-primary-foreground px-1.5 py-0.5 rounded-full backdrop-blur-sm">
              Global
            </span>
          )}
          {section.is_custom && (
            <span className="text-xs bg-purple-500/80 text-white px-1.5 py-0.5 rounded-full backdrop-blur-sm">
              Custom
            </span>
          )}
        </div>
      </div>

      {/* Section Name at Bottom */}
      <div className="p-3">
        <h4 className="text-sm font-medium text-foreground group-hover:text-primary line-clamp-2 mb-1">
          {section.name}
        </h4>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground capitalize">
            {section.type}
          </p>
        </div>
      </div>
    </div>
  );
}
